import React, { useCallback, useEffect, useState, useMemo } from "react";
import useAsyncMemo from "../../hooks/useAsyncMemo";
import { createDatePrettyName, isBlank } from "../../utils/strings";
import PageLayout from "../common/PageLayout";
import { useNavigate } from "react-router";
import StepView from "./StepView";
import { useToast } from "../shadcn/useToast";
import SearchImportedListings from "./SearchImportedListings";
import { STEPS, STEPS_CONFIG, LOOKUP_TYPES, MAX_UPLOAD_RECORDS } from "./constants";
import { convertFileToJson } from "./utils";
import ConfigureUpload from "./ConfigureUpload";
import Review from "./Review";
import MapFields from "./MapFields";
import NoAccessPage from "../common/noAccessPage";
import { useAuth } from "../utils/auth/assist/authContext";
import {
  useUploadMutation,
  useGetCustomUploadsQueryCreditsQuery,
  checkIsValidImportNameQuery,
} from "../../hooks/useCustomUploads";

const fieldsConfig = [
  { label: "First Name", key: "first_name", isRecommended: true },
  { label: "Last Name", key: "last_name", isRecommended: true },
  { label: "Address", key: "full_address" },
  { label: "City", key: "city" },
  { label: "State", key: "state" },
  { label: "Zip Code", key: "postal_code" },
  {
    label: "Primary Phone",
    mappedKey: "",

    key: "primary_phone_number",
  },
  {
    label: "Secondary Phone",
    mappedKey: "",

    key: "secondary_phone_number",
  },
  { label: "Email", key: "email" },
  { label: "Price", key: "price" },
  { label: "Beds", key: "bedrooms" },
  { label: "Baths", key: "baths" },
  { label: "Square Footage", key: "square_footage" },
  { label: "Lots Size", key: "lot_size" },
];

const createMapping = () =>
  fieldsConfig.map(({ key }) => ({
    mappedKey: "",
    key,
  }));

const createDefaultImportName = () => createDatePrettyName("Import");

const ManageImports = () => {
  const { mutateAsync: upload } = useUploadMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jsonData, setJsonData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [currentStep, setCurrentStep] = useState(STEPS.configureUpload);
  const [listName, setListName] = useState(createDefaultImportName());
  const [listSource, setListSource] = useState();
  const [lookupType, setLookupType] = useState(LOOKUP_TYPES.reverseAddress);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [mapping, setMapping] = useState(createMapping());
  const [currentUploadId, setCurrentUploadId] = useState();
  const { data: userCredits } = useGetCustomUploadsQueryCreditsQuery();
  const { user } = useAuth();
  const numRecords = useMemo(() => jsonData?.length || 0, [jsonData]);

  // Determine if this is a single entry lookup (phone or address) vs file upload
  const isSingleEntryLookup = useMemo(
    () => !fileName && (!!selectedAddress || !!phoneNumber),
    [fileName, selectedAddress, phoneNumber]
  );

  const augmentedFieldsConfig = useMemo(
    () =>
      fieldsConfig
        .map((field) => {
          const { key } = field;
          switch (key) {
            case "primary_phone_number":
              return {
                ...field,
                isRequired: lookupType === LOOKUP_TYPES.reversePhone,
              };
            case "full_address":
            case "city":
            case "state":
            case "postal_code":
              return {
                ...field,
                isRequired: lookupType === LOOKUP_TYPES.reverseAddress,
              };
            default:
              return {
                ...field,
                isRequired: false,
              };
          }
        })
        .sort((field1, field2) => {
          const { isRequired: isRequired1, isRecommended: isRecommended1 } =
            field1;
          const { isRequired: isRequired2, isRecommended: isRecommended2 } =
            field2;
          if (isRequired1 && !isRequired2) {
            return -1;
          }
          if (isRequired2 && !isRequired1) {
            return 1;
          }
          if (isRecommended1 && !isRecommended2) {
            return -1;
          }
          if (isRecommended2 && !isRecommended1) {
            return 1;
          }
          return 0;
        }),
    [fieldsConfig, lookupType]
  );

  const stepsInfo = useAsyncMemo(async () => {
    const isValidImportName =
      !isBlank(listName) && (await checkIsValidImportNameQuery(listName));

    // Check if we have a valid single entry (address or phone)
    const hasValidSingleEntry =
      (lookupType === LOOKUP_TYPES.reverseAddress && !!selectedAddress) ||
      (lookupType === LOOKUP_TYPES.reversePhone && !!phoneNumber);

    return STEPS_CONFIG.map((stepConfig) => {
      let navigateToPermitted;
      switch (stepConfig.name) {
        case STEPS.review:
          if (hasValidSingleEntry) {
            // For single entry, skip mapping check
            navigateToPermitted = isValidImportName && userCredits >= 1;
          } else {
            const requiredFieldKeys = augmentedFieldsConfig
              .filter(({ isRequired }) => isRequired)
              .map(({ key }) => key);
            const hasMissingFields = mapping.some(
              ({ mappedKey, key }) =>
                requiredFieldKeys.includes(key) && !mappedKey
            );
            navigateToPermitted =
              !!fileName && !hasMissingFields && isValidImportName;
          }
          break;
        case STEPS.mapFields:
          // Skip mapFields for single entry lookups
          if (hasValidSingleEntry) {
            navigateToPermitted = false;
          } else {
            navigateToPermitted =
              !!fileName && userCredits >= numRecords && isValidImportName;
          }
          break;
        default:
          navigateToPermitted = true;
          break;
      }

      return {
        ...stepConfig,
        navigateToPermitted,
      };
    });
  }, [
    currentStep,
    augmentedFieldsConfig,
    fileName,
    mapping,
    numRecords,
    userCredits,
    listName,
    lookupType,
    selectedAddress,
    phoneNumber,
  ]);

  const currentStepInfo = useMemo(
    () => STEPS_CONFIG.find(({ name }) => name === currentStep),
    [currentStep]
  );

  const fileColumnsKeys = useMemo(
    () => Object.keys(jsonData?.[0] || {}).map((key) => key.trim()),
    [jsonData]
  );

  const handleStepClick = useCallback(
    (step) => {
      setCurrentStep(step);
    },
    [setCurrentStep]
  );

  const handleSubmitClick = useCallback(async () => {
    try {
      let submitData = jsonData;
      let submitMapping = mapping;
      let submitFileColumnsKeys = fileColumnsKeys;

      // For single entry lookups, create a single record
      if (isSingleEntryLookup) {
        if (lookupType === LOOKUP_TYPES.reverseAddress && selectedAddress) {
          submitData = [{
            full_address: selectedAddress.formattedAddress,
            city: selectedAddress.address?.city || "",
            state: selectedAddress.address?.state || "",
            postal_code: selectedAddress.address?.zipcode || "",
          }];
          submitMapping = [
            { key: "full_address", mappedKey: "full_address" },
            { key: "city", mappedKey: "city" },
            { key: "state", mappedKey: "state" },
            { key: "postal_code", mappedKey: "postal_code" },
          ];
          submitFileColumnsKeys = ["full_address", "city", "state", "postal_code"];
        } else if (lookupType === LOOKUP_TYPES.reversePhone && phoneNumber) {
          submitData = [{
            primary_phone_number: phoneNumber,
          }];
          submitMapping = [
            { key: "primary_phone_number", mappedKey: "primary_phone_number" },
          ];
          submitFileColumnsKeys = ["primary_phone_number"];
        }
      }

      upload({
        config: {
          mapping: submitMapping,
          fileColumnsKeys: submitFileColumnsKeys,
          listSource: "" || listSource?.value || "",
          fileName,
          listName,
          lookupType,
        },
        jsonData: submitData,
      });
      navigate("/data-genie/previous");
      toast({
        title: `Lookup submitted`,
        description: `${listName || fileName} has been submitted for lookup`,
        variant: "success",
      });
    } catch (error) {
      console.error(error);
    }
  }, [upload, mapping, listSource, fileName, listName, jsonData, isSingleEntryLookup, lookupType, selectedAddress, phoneNumber, fileColumnsKeys]);

  const handleCancelClick = useCallback(() => {
    navigate("/data-genie/previous");
  }, []);

  const handleFileSelected = useCallback(
    async (file) => {
      if (!file) {
        return;
      }
      setFileName(file.name);
      const jsonData = await convertFileToJson(file);
      if (!jsonData) {
        return;
      }
      // Check if file exceeds max records
      if (jsonData.length > MAX_UPLOAD_RECORDS) {
        toast({
          title: "File too large",
          description: `Your file contains ${jsonData.length.toLocaleString()} records. The maximum allowed is ${MAX_UPLOAD_RECORDS.toLocaleString()} records.`,
          variant: "destructive",
        });
        setFileName("");
        setJsonData(null);
        return;
      }
      setJsonData(jsonData);
      // Clear single entry selections when file is selected
      setSelectedAddress(null);
      setPhoneNumber("");
      if (jsonData.length > userCredits) {
        // Show error toast
      }
    },
    [setFileName, setJsonData, userCredits, toast]
  );

  const handleMappingChange = useCallback(
    ({ key, mappedKey }) => {
      setMapping((prevMapping) =>
        prevMapping.map((field) => {
          return field.key === key ? { ...field, mappedKey } : field;
        })
      );
    },
    [jsonData, setMapping]
  );

  const handleAddressSelected = useCallback(
    (addressData) => {
      setSelectedAddress(addressData);
      // Clear file selection when address is selected
      setFileName("");
      setJsonData(null);
      // Update list name to include address
      if (addressData?.formattedAddress) {
        setListName( addressData.formattedAddress);
      }
    },
    [setSelectedAddress]
  );

  const handlePhoneNumberChange = useCallback(
    (phone) => {
      setPhoneNumber(phone);
      // Clear file selection when phone is entered
      if (phone) {
        setFileName("");
        setJsonData(null);
        // Update list name to include phone number
        setListName(phone);
      }
    },
    [setPhoneNumber]
  );

  const handleLookupTypeChange = useCallback(
    (newLookupType) => {
      setLookupType(newLookupType);
      // Clear single-entry inputs when switching lookup types
      setSelectedAddress(null);
      setPhoneNumber("");
    },
    [setLookupType]
  );

  return (
    <>
      <PageLayout zeroMargin={false} suppressTopPadding>
        {!!user?.customupload ? (
          currentStep === STEPS.review ? (
            <Review
              onSubmitClick={handleSubmitClick}
              onCancelClick={handleCancelClick}
              onBackClick={() => {
                if (isSingleEntryLookup) {
                  setCurrentStep(STEPS.configureUpload);
                } else {
                  setCurrentStep(STEPS.mapFields);
                }
              }}
              mapping={mapping}
              fieldsConfig={augmentedFieldsConfig}
              numRecords={isSingleEntryLookup ? 1 : jsonData.length}
              showMappings={!isSingleEntryLookup}
              selectedAddress={selectedAddress}
              phoneNumber={phoneNumber}
              lookupType={lookupType}
            />
          ) : (
            [STEPS.configureUpload, STEPS.mapFields].includes(currentStep) && (
              <StepView
                onStepClick={handleStepClick}
                stepsInfo={stepsInfo}
                onCancelClick={handleCancelClick}
                isLastStep={false}
                isFirstStep={currentStep === STEPS.configureUpload}
                nextStep={isSingleEntryLookup && currentStep === STEPS.configureUpload ? STEPS.review : undefined}
                onPrevClick={() => {
                  setCurrentStep(currentStep - 1);
                }}
                onNextClick={() => {
                  // Skip mapFields step for single entry lookups
                  if (isSingleEntryLookup && currentStep === STEPS.configureUpload) {
                    setCurrentStep(STEPS.review);
                  } else {
                    setCurrentStep(currentStep + 1);
                  }
                }}
                currentStep={currentStep}
                currentStepInfo={currentStepInfo}
                userCredits={userCredits}
              >
                {currentStep === STEPS.configureUpload && (
                  <ConfigureUpload
                    importNameValidator={checkIsValidImportNameQuery}
                    numRecords={numRecords}
                    jsonData={jsonData}
                    onFileSelected={handleFileSelected}
                    onListNameChange={setListName}
                    fileName={fileName}
                    userCredits={userCredits}
                    listName={listName}
                    fileColumnsKeys={fileColumnsKeys}
                    listSource={listSource}
                    onListSourceChange={setListSource}
                    lookupType={lookupType}
                    onLookupTypeChange={handleLookupTypeChange}
                    phoneNumber={phoneNumber}
                    onPhoneNumberChange={handlePhoneNumberChange}
                    onAddressSelected={handleAddressSelected}
                    selectedAddress={selectedAddress}
                  />
                )}
                {currentStep === STEPS.mapFields && (
                  <MapFields
                    fileColumnsKeys={fileColumnsKeys}
                    mapping={mapping}
                    fieldsConfig={augmentedFieldsConfig}
                    onMappingChange={handleMappingChange}
                  />
                )}
              </StepView>
            )
          )
        ) : (
          <NoAccessPage product="Data Genie" />
        )}
      </PageLayout>
    </>
  );
};

export default ManageImports;
